import type {
  SpreadsheetDefinedNameEntry,
  SpreadsheetDefinedNameScope,
  SpreadsheetNamedRangeEntry,
  SpreadsheetRangeAddress,
  SpreadsheetSheetListEntry,
} from '../protocol/spreadsheetEngineProtocol'
import { buildSpreadsheetEngineSheetId } from '../protocol/spreadsheetEngineProtocol'
import {
  normalizeSelectionRange,
  parseA1Selection,
  type SpreadsheetSelectionRange,
} from './addressing'
import type { SpreadsheetCommandId } from './commandRegistry'
import { formatFormulaRangeRef } from './formulaAuthoring'

const DEFINED_NAME_RE = /^[A-Za-z_\\][A-Za-z0-9_.\\]*$/

export type DefinedNameCreateDraft = {
  name: string
  scope: SpreadsheetDefinedNameScope
  refersTo: string
  range: SpreadsheetRangeAddress
  comment?: string | null
  hidden?: boolean
}

export type CreateNamesFromSelectionOptions = {
  topRow: boolean
  leftColumn: boolean
  bottomRow: boolean
  rightColumn: boolean
}

export type CreateNamesFromSelectionOptionKey = keyof CreateNamesFromSelectionOptions

export type CreateNamesFromSelectionOptionPresentation = {
  key: CreateNamesFromSelectionOptionKey
  label: string
  checked: boolean
}

export type CreateNamesFromSelectionDialogPresentation = {
  ariaLabel: string
  title: string
  closeAriaLabel: string
  closeTitle: string
  selectionLabel: string
  selectionTitle: string
  optionsLegend: string
  options: CreateNamesFromSelectionOptionPresentation[]
  cancelLabel: string
  createLabel: string
  noSelectionError: string
  selectedCount: number
}

export type DefinedNameManagerDraft = {
  name: string
  scope: SpreadsheetDefinedNameScope
  refersTo: string
  range?: SpreadsheetRangeAddress | null
  comment?: string | null
  hidden?: boolean
}

export type DefinedNameManagerEditorDraft = {
  name: string
  scope: SpreadsheetDefinedNameScope
  refersTo: string
  comment: string
  hidden: boolean
}

export type DefinedNameManagerFilterMode = 'all' | 'workbook' | 'sheet' | 'errors' | 'hidden'

export type DefinedNameManagerRowPresentation = {
  key: string
  name: string
  value: string
  refersTo: string
  scopeLabel: string
  comment: string
  hasError: boolean
  canSelect: boolean
  editTitle: string
  editAriaLabel: string
  selectTitle: string
  selectAriaLabel: string
  deleteTitle: string
  deleteAriaLabel: string
}

export type DefinedNameManagerCommitResult =
  | { ok: true; draft: DefinedNameManagerDraft }
  | { ok: false; error: string }

export type FormulaReferenceSuggestion = {
  name: string
  scopeLabel?: string
  refersTo?: string
  hidden?: boolean
}

export type UseInFormulaMenuDescriptor = {
  id: string
  label: string
  title: string
  insertText: string
}

export type DefinedNameCommandState = {
  enabled: boolean
  disabledReason: string
}

export type DefinedNamesApplyResultNotice = {
  x: number
  y: number
  title: string
  message: string
}

export const DEFAULT_CREATE_NAMES_FROM_SELECTION_OPTIONS: CreateNamesFromSelectionOptions = {
  topRow: true,
  leftColumn: false,
  bottomRow: false,
  rightColumn: false,
}

const CREATE_NAMES_FROM_SELECTION_OPTION_DEFINITIONS: Array<{
  key: CreateNamesFromSelectionOptionKey
  label: string
}> = [
  { key: 'topRow', label: 'Top row' },
  { key: 'leftColumn', label: 'Left column' },
  { key: 'bottomRow', label: 'Bottom row' },
  { key: 'rightColumn', label: 'Right column' },
]

export function buildDefinedNameSheetNameMaps(sheets: SpreadsheetSheetListEntry[]): {
  sheetNameByEngineId: Map<string, string>
  sheetIdByName: Map<string, string>
} {
  const sheetNameByEngineId = new Map<string, string>()
  const sheetIdByName = new Map<string, string>()
  for (const sheet of sheets) {
    const engineId = buildSpreadsheetEngineSheetId(sheet.id)
    sheetNameByEngineId.set(engineId, sheet.name)
    sheetIdByName.set(sheet.name.toLowerCase(), engineId)
  }
  return { sheetNameByEngineId, sheetIdByName }
}

export function normalizeCreateNamesFromSelectionOptions(
  options?: Partial<CreateNamesFromSelectionOptions> | null,
): CreateNamesFromSelectionOptions {
  return {
    ...DEFAULT_CREATE_NAMES_FROM_SELECTION_OPTIONS,
    ...(options ?? {}),
  }
}

export function countCreateNamesFromSelectionLabelLocations(options: CreateNamesFromSelectionOptions): number {
  return Number(options.topRow) + Number(options.leftColumn) + Number(options.bottomRow) + Number(options.rightColumn)
}

export function toggleCreateNamesFromSelectionOption(
  options: CreateNamesFromSelectionOptions,
  key: CreateNamesFromSelectionOptionKey,
): CreateNamesFromSelectionOptions {
  return {
    ...options,
    [key]: !options[key],
  }
}

export function validateCreateNamesFromSelectionOptions(
  options: CreateNamesFromSelectionOptions,
): string | null {
  return countCreateNamesFromSelectionLabelLocations(options) > 0
    ? null
    : 'Choose at least one label location.'
}

export function buildCreateNamesFromSelectionDialogPresentation(args: {
  selectionLabel: string
  options: CreateNamesFromSelectionOptions
}): CreateNamesFromSelectionDialogPresentation {
  const selectionLabel = String(args.selectionLabel || '').trim() || 'Current selection'
  return {
    ariaLabel: 'Create Names from Selection',
    title: 'Create Names from Selection',
    closeAriaLabel: 'Close Create Names',
    closeTitle: 'Close Create Names',
    selectionLabel,
    selectionTitle: selectionLabel,
    optionsLegend: 'Create names from values in the',
    options: CREATE_NAMES_FROM_SELECTION_OPTION_DEFINITIONS.map((option) => ({
      ...option,
      checked: args.options[option.key] === true,
    })),
    cancelLabel: 'Cancel',
    createLabel: 'Create',
    noSelectionError: 'Choose at least one label location.',
    selectedCount: countCreateNamesFromSelectionLabelLocations(args.options),
  }
}

export function normalizeDefinedNameScope(scope?: SpreadsheetDefinedNameScope | null): SpreadsheetDefinedNameScope {
  return scope?.kind === 'sheet'
    ? { kind: 'sheet', sheet_id: String(scope.sheet_id || '').trim() }
    : { kind: 'workbook' }
}

export function formatDefinedNameRangeFormula(range: SpreadsheetRangeAddress, sheetName?: string | null): string {
  const ref = formatFormulaRangeRef({
    sheetName: sheetName || undefined,
    start: { col: range.start_col, row: range.start_row },
    end: { col: range.end_col, row: range.end_row },
  })
  return `=${ref}`
}

export function scopeKeyForDefinedName(scope?: SpreadsheetDefinedNameScope | null): string {
  const normalized = normalizeDefinedNameScope(scope)
  if (normalized.kind === 'sheet') return `sheet:${normalized.sheet_id.toLowerCase()}`
  return 'workbook'
}

export function scopeLabelForDefinedName(
  scope: SpreadsheetDefinedNameScope,
  sheetNameByEngineId: Map<string, string>,
): string {
  return scope.kind === 'sheet'
    ? (sheetNameByEngineId.get(scope.sheet_id) ?? scope.sheet_id)
    : 'Workbook'
}

export function getVisibleDefinedNames(definedNames: SpreadsheetDefinedNameEntry[]): SpreadsheetDefinedNameEntry[] {
  return definedNames
    .filter((entry) => entry.hidden !== true)
    .slice()
    .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }))
}

export function buildDefinedNamedRanges(definedNames: SpreadsheetDefinedNameEntry[]): SpreadsheetNamedRangeEntry[] {
  return definedNames
    .filter((entry): entry is SpreadsheetDefinedNameEntry & { range: SpreadsheetRangeAddress } => Boolean(entry.range))
    .map((entry) => ({ name: entry.name, range: entry.range }))
}

export function buildFormulaDefinedNameSuggestions(
  definedNames: SpreadsheetDefinedNameEntry[],
  sheetNameByEngineId: Map<string, string>,
): FormulaReferenceSuggestion[] {
  return definedNames.map((entry) => ({
    name: entry.name,
    scopeLabel: scopeLabelForDefinedName(entry.scope, sheetNameByEngineId),
    refersTo: entry.refers_to,
    hidden: entry.hidden,
  }))
}

export function buildSelectedDefinedNameRangeFormula(
  selectedRange: SpreadsheetRangeAddress,
  sheetNameByEngineId: Map<string, string>,
): string {
  return formatDefinedNameRangeFormula(selectedRange, sheetNameByEngineId.get(selectedRange.sheet_id))
}

export function buildDefinedNameManagerEditorDraft(args: {
  entry?: SpreadsheetDefinedNameEntry | null
  selectedRangeFormula: string
  sheetNameByEngineId: Map<string, string>
}): DefinedNameManagerEditorDraft {
  const entry = args.entry ?? null
  if (!entry) {
    return {
      name: '',
      scope: { kind: 'workbook' },
      refersTo: args.selectedRangeFormula,
      comment: '',
      hidden: false,
    }
  }
  return {
    name: entry.name,
    scope: normalizeDefinedNameScope(entry.scope),
    refersTo: entry.refers_to || (entry.range ? formatDefinedNameRangeFormula(entry.range, args.sheetNameByEngineId.get(entry.range.sheet_id)) : ''),
    comment: entry.comment ?? '',
    hidden: entry.hidden === true,
  }
}

export function filterDefinedNameManagerEntries(args: {
  definedNames: SpreadsheetDefinedNameEntry[]
  filterMode: DefinedNameManagerFilterMode
  activeSheetId: string
}): SpreadsheetDefinedNameEntry[] {
  return args.definedNames.filter((entry) => {
    if (args.filterMode === 'workbook') return entry.scope.kind === 'workbook'
    if (args.filterMode === 'sheet') return entry.scope.kind === 'sheet' && entry.scope.sheet_id === args.activeSheetId
    if (args.filterMode === 'errors') return Boolean(entry.error || /#REF!/i.test(entry.refers_to))
    if (args.filterMode === 'hidden') return entry.hidden === true
    return true
  })
}

export function buildDefinedNameManagerRows(args: {
  entries: SpreadsheetDefinedNameEntry[]
  sheetNameByEngineId: Map<string, string>
}): DefinedNameManagerRowPresentation[] {
  return args.entries.map((entry) => {
    const refersTo = entry.refers_to || (entry.range ? formatDefinedNameRangeFormula(entry.range, args.sheetNameByEngineId.get(entry.range.sheet_id)) : '')
    return {
      key: `${scopeKeyForDefinedName(entry.scope)}:${entry.name.toLowerCase()}`,
      name: entry.name,
      value: formatDefinedNameValue(entry),
      refersTo,
      scopeLabel: scopeLabelForDefinedName(entry.scope, args.sheetNameByEngineId),
      comment: entry.comment ?? '',
      hasError: Boolean(entry.error),
      canSelect: Boolean(entry.range),
      editTitle: `Edit ${entry.name}`,
      editAriaLabel: `Edit ${entry.name}`,
      selectTitle: `Go to ${entry.name}`,
      selectAriaLabel: `Go to ${entry.name}`,
      deleteTitle: `Delete ${entry.name}`,
      deleteAriaLabel: `Delete ${entry.name}`,
    }
  })
}

export function buildDefinedNameManagerCommitDraft(args: {
  nameDraft: string
  scopeDraft: SpreadsheetDefinedNameScope
  refersToDraft: string
  commentDraft: string
  hiddenDraft: boolean
  definedNames: SpreadsheetDefinedNameEntry[]
  editingEntry?: SpreadsheetDefinedNameEntry | null
  activeSheetId: string
  rowCount: number
  colCount: number
  sheetIdByName: Map<string, string>
}): DefinedNameManagerCommitResult {
  const name = String(args.nameDraft || '').trim()
  const nameError = validateDefinedName(name)
  if (nameError) return { ok: false, error: nameError }

  const scope = normalizeDefinedNameScope(args.scopeDraft)
  const duplicate = args.definedNames.find((entry) => (
    entry.name.toLowerCase() === name.toLowerCase()
    && scopeKeyForDefinedName(entry.scope) === scopeKeyForDefinedName(scope)
    && !(args.editingEntry
      && entry.name.toLowerCase() === args.editingEntry.name.toLowerCase()
      && scopeKeyForDefinedName(entry.scope) === scopeKeyForDefinedName(args.editingEntry.scope))
  ))
  if (duplicate) return { ok: false, error: 'A name already exists in that scope.' }

  const refersTo = normalizeDefinedNameRefersTo(args.refersToDraft)
  if (!refersTo) return { ok: false, error: 'Refers To is required.' }

  return {
    ok: true,
    draft: {
      name,
      scope,
      refersTo,
      range: parseDefinedNameRangeDraft({
        input: refersTo,
        activeSheetId: args.activeSheetId,
        rowCount: args.rowCount,
        colCount: args.colCount,
        sheetIdByName: args.sheetIdByName,
      }),
      comment: args.commentDraft.trim() || null,
      hidden: args.hiddenDraft,
    },
  }
}

export function buildUseInFormulaMenuDescriptors(args: {
  visibleDefinedNames: SpreadsheetDefinedNameEntry[]
  structuredReferenceSuggestions?: Array<{ name: string; refersTo?: string }>
  sheetNameByEngineId: Map<string, string>
}): UseInFormulaMenuDescriptor[] {
  const counts = new Map<string, number>()
  for (const entry of args.visibleDefinedNames) {
    const key = entry.name.toLowerCase()
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  const definedNameItems = args.visibleDefinedNames.map((entry) => {
    const duplicate = (counts.get(entry.name.toLowerCase()) ?? 0) > 1
    const scopeLabel = scopeLabelForDefinedName(entry.scope, args.sheetNameByEngineId)
    return {
      id: `${scopeKeyForDefinedName(entry.scope)}:${entry.name}`,
      label: duplicate ? `${entry.name} (${scopeLabel})` : entry.name,
      title: entry.refers_to || entry.name,
      insertText: entry.name,
    }
  })
  const tableReferenceItems = (args.structuredReferenceSuggestions ?? []).map((entry) => ({
    id: `structured-reference:${entry.name}`,
    label: entry.name,
    title: entry.refersTo || entry.name,
    insertText: entry.name,
  }))
  return [...definedNameItems, ...tableReferenceItems]
}

export function buildDefinedNameCommandPresentation(args: {
  hasSelection: boolean
  hasStreamer: boolean
  definedNameCount: number
  useInFormulaItemCount: number
  hasVisibleRangeName: boolean
}): Partial<Record<SpreadsheetCommandId, DefinedNameCommandState>> {
  const selectionAndStreamer = args.hasSelection && args.hasStreamer
  return {
    'name-manager': {
      enabled: args.hasStreamer,
      disabledReason: 'Spreadsheet engine is not ready',
    },
    'define-name': {
      enabled: selectionAndStreamer,
      disabledReason: 'Select cells after the spreadsheet engine is ready',
    },
    'use-in-formula': {
      enabled: selectionAndStreamer && args.useInFormulaItemCount > 0,
      disabledReason: args.useInFormulaItemCount > 0
        ? 'Select a cell after the spreadsheet engine is ready'
        : 'Create a defined name or table first',
    },
    'create-from-selection': {
      enabled: selectionAndStreamer,
      disabledReason: 'Select a labelled range after the spreadsheet engine is ready',
    },
    'paste-names': {
      enabled: selectionAndStreamer && args.definedNameCount > 0,
      disabledReason: args.definedNameCount > 0
        ? 'Select a destination cell after the spreadsheet engine is ready'
        : 'Create a defined name first',
    },
    'apply-names': {
      enabled: selectionAndStreamer && args.hasVisibleRangeName,
      disabledReason: 'Select formulas that reference a defined range',
    },
  }
}

export function buildApplyNamesResultNotice(args: {
  replacementCount: number
}): DefinedNamesApplyResultNotice | null {
  return args.replacementCount === 0
    ? {
        x: 16,
        y: 16,
        title: 'Apply Names',
        message: 'No selected formulas matched an existing defined-name reference.',
      }
    : null
}

export function buildDefinedNamesPasteTsv(args: {
  definedNames: SpreadsheetDefinedNameEntry[]
  sheetNameByEngineId: Map<string, string>
}): string {
  const rows = [
    ['Name', 'Refers To', 'Scope', 'Comment'],
    ...args.definedNames.map((entry) => [
      entry.name,
      entry.refers_to || (entry.range ? formatDefinedNameRangeFormula(entry.range, args.sheetNameByEngineId.get(entry.range.sheet_id)) : ''),
      scopeLabelForDefinedName(entry.scope, args.sheetNameByEngineId),
      entry.comment ?? '',
    ]),
  ]
  return rows.map((row) => row.map(escapeTsvCell).join('\t')).join('\n')
}

export function buildCreateNamesFromSelectionDrafts(input: {
  selection: SpreadsheetSelectionRange
  activeSheetId: string
  activeSheetName?: string | null
  existingNames: SpreadsheetDefinedNameEntry[]
  getCellValue: (col: number, row: number) => string | undefined
  options: CreateNamesFromSelectionOptions
}): DefinedNameCreateDraft[] {
  const selection = normalizeSelectionRange(input.selection)
  const dataStartRow = selection.row + (input.options.topRow ? 1 : 0)
  const dataEndRow = selection.endRow - (input.options.bottomRow ? 1 : 0)
  const dataStartCol = selection.col + (input.options.leftColumn ? 1 : 0)
  const dataEndCol = selection.endCol - (input.options.rightColumn ? 1 : 0)
  if (dataStartRow > dataEndRow || dataStartCol > dataEndCol) {
    throw new Error('The selected range does not contain data cells after removing label rows or columns.')
  }

  const scope: SpreadsheetDefinedNameScope = { kind: 'workbook' }
  const usedNames = new Set(
    input.existingNames
      .filter((entry) => scopeKeyForDefinedName(entry.scope) === 'workbook')
      .map((entry) => entry.name.toLowerCase()),
  )
  const drafts: DefinedNameCreateDraft[] = []
  const addDraft = (rawLabel: string | undefined, fallback: string, range: SpreadsheetRangeAddress) => {
    if (!String(rawLabel ?? '').trim()) return
    const baseName = sanitizeDefinedNameLabel(rawLabel ?? '', fallback)
    const name = uniqueDefinedNameLabel(baseName, usedNames)
    drafts.push({
      name,
      scope,
      refersTo: formatDefinedNameRangeFormula(range, input.activeSheetName),
      range,
      comment: null,
      hidden: false,
    })
  }

  if (input.options.topRow) {
    for (let col = dataStartCol; col <= dataEndCol; col += 1) {
      addDraft(input.getCellValue(col, selection.row), `Column_${col + 1}`, {
        sheet_id: input.activeSheetId,
        start_col: col,
        end_col: col,
        start_row: dataStartRow,
        end_row: dataEndRow,
      })
    }
  }
  if (input.options.bottomRow) {
    for (let col = dataStartCol; col <= dataEndCol; col += 1) {
      addDraft(input.getCellValue(col, selection.endRow), `Column_${col + 1}`, {
        sheet_id: input.activeSheetId,
        start_col: col,
        end_col: col,
        start_row: dataStartRow,
        end_row: dataEndRow,
      })
    }
  }
  if (input.options.leftColumn) {
    for (let row = dataStartRow; row <= dataEndRow; row += 1) {
      addDraft(input.getCellValue(selection.col, row), `Row_${row + 1}`, {
        sheet_id: input.activeSheetId,
        start_col: dataStartCol,
        end_col: dataEndCol,
        start_row: row,
        end_row: row,
      })
    }
  }
  if (input.options.rightColumn) {
    for (let row = dataStartRow; row <= dataEndRow; row += 1) {
      addDraft(input.getCellValue(selection.endCol, row), `Row_${row + 1}`, {
        sheet_id: input.activeSheetId,
        start_col: dataStartCol,
        end_col: dataEndCol,
        start_row: row,
        end_row: row,
      })
    }
  }

  if (drafts.length === 0) throw new Error('No labels were found in the selected range.')
  return drafts
}

function normalizeDefinedNameRefersTo(input: string): string {
  const text = String(input || '').trim()
  if (!text) return ''
  return text.startsWith('=') ? text : `=${text}`
}

function unquoteSheetToken(token: string): string {
  const trimmed = String(token || '').trim()
  if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
    return trimmed.slice(1, -1).replace(/''/g, "'")
  }
  return trimmed
}

function parseDefinedNameRangeDraft(args: {
  input: string
  activeSheetId: string
  rowCount: number
  colCount: number
  sheetIdByName: Map<string, string>
}): SpreadsheetRangeAddress | null {
  const text = String(args.input || '').trim().replace(/^=/, '').trim()
  if (!text) return null
  const bangIndex = text.lastIndexOf('!')
  const sheetId = bangIndex >= 0
    ? (() => {
        const token = unquoteSheetToken(text.slice(0, bangIndex))
        if (/^sheet_[1-9]\d*$/i.test(token)) return token
        return args.sheetIdByName.get(token.toLowerCase()) ?? args.activeSheetId
      })()
    : args.activeSheetId
  const selectionText = (bangIndex >= 0 ? text.slice(bangIndex + 1) : text).replace(/\s+/g, '')
  const parsed = parseA1Selection(selectionText, { rowCount: args.rowCount, colCount: args.colCount })
  if (!parsed) return null
  return {
    sheet_id: sheetId,
    start_row: Math.min(parsed.row, parsed.endRow),
    start_col: Math.min(parsed.col, parsed.endCol),
    end_row: Math.max(parsed.row, parsed.endRow),
    end_col: Math.max(parsed.col, parsed.endCol),
  }
}

function validateDefinedName(input: string): string | null {
  const name = String(input || '').trim()
  if (!name) return 'Name is required.'
  if (!DEFINED_NAME_RE.test(name)) return 'Start with a letter, underscore, or backslash. Use letters, numbers, periods, and underscores.'
  if (parseA1Selection(name)) return 'Name cannot be a cell or range address.'
  return null
}

function formatDefinedNameValue(entry: SpreadsheetDefinedNameEntry): string {
  if (entry.error) return entry.error
  const value = entry.value
  if (value == null) return ''
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (typeof value === 'object' && 'kind' in value) {
    if (value.kind === 'error') return value.code
    if (value.kind === 'number') return String(value.value)
    if (value.kind === 'text') return value.value
    if (value.kind === 'boolean') return value.value ? 'TRUE' : 'FALSE'
    if (value.kind === 'blank') return ''
  }
  return ''
}

function sanitizeDefinedNameLabel(raw: string, fallback: string): string {
  const source = String(raw || '').trim() || fallback
  let name = source
    .replace(/^=/, '')
    .replace(/['"]/g, '')
    .replace(/[^A-Za-z0-9_.\\]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
  if (!name) name = fallback
  if (!/^[A-Za-z_\\]/.test(name)) name = `_${name}`
  if (!DEFINED_NAME_RE.test(name)) name = name.replace(/[^A-Za-z0-9_.\\]/g, '_')
  if (parseA1Selection(name)) name = `${name}_name`
  return name
}

function uniqueDefinedNameLabel(baseName: string, existingNames: Set<string>): string {
  let name = baseName
  let suffix = 2
  while (existingNames.has(name.toLowerCase())) {
    name = `${baseName}_${suffix}`
    suffix += 1
  }
  existingNames.add(name.toLowerCase())
  return name
}

function escapeTsvCell(value: string): string {
  return String(value ?? '').replace(/\t/g, ' ').replace(/\r?\n/g, ' ')
}
